import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Heart, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function PasswordResetPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== passwordConfirm) {
            toast.error('Passwörter stimmen nicht überein');
            return;
        }
        if (password.length < 6) {
            toast.error('Passwort muss mindestens 6 Zeichen lang sein');
            return;
        }
        setLoading(true);
        try {
            await authApi.resetPassword(token, password);
            setSuccess(true);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Zurücksetzen des Passworts');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-gray-50">
                <header className="bg-secondary">
                    <div className="max-w-md mx-auto px-4 py-4">
                        <Link to="/seminare" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm">
                            <ArrowLeft className="h-4 w-4" />
                            Zurück
                        </Link>
                    </div>
                </header>
                <div className="max-w-md mx-auto px-4 py-16 text-center">
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Ungültiger Link</h2>
                    <p className="text-gray-500 mb-6">Dieser Link ist ungültig oder abgelaufen.</p>
                    <Link to="/teilnehmer/login" className="text-primary hover:underline font-medium">
                        Zum Login
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <header className="bg-secondary">
                <div className="max-w-md mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <Link to="/seminare" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm">
                            <ArrowLeft className="h-4 w-4" />
                            Zurück
                        </Link>
                        <div className="flex items-center gap-2">
                            <Heart className="h-5 w-5 text-primary" />
                            <span className="text-sm font-semibold text-white">BGPR</span>
                        </div>
                    </div>
                </div>
            </header>

            <div className="flex items-center justify-center p-4 pt-12">
                <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
                    {success ? (
                        <div className="text-center py-4" data-testid="reset-success">
                            <div className="mx-auto mb-4 p-3 bg-green-100 rounded-xl w-fit">
                                <CheckCircle className="h-8 w-8 text-green-600" />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">Passwort geändert</h2>
                            <p className="text-gray-500 mb-6">Ihr Passwort wurde erfolgreich geändert. Sie können sich jetzt anmelden.</p>
                            <Button
                                onClick={() => navigate('/teilnehmer/login')}
                                className="bg-primary hover:bg-primary/80"
                                data-testid="go-to-login"
                            >
                                Zum Login
                            </Button>
                        </div>
                    ) : (
                        <>
                            <h2 className="text-xl font-bold text-gray-900 mb-1">Neues Passwort festlegen</h2>
                            <p className="text-sm text-gray-500 mb-6">Geben Sie Ihr neues Passwort ein.</p>
                            <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
                                <div className="space-y-2">
                                    <Label htmlFor="new_password">Neues Passwort * <span className="font-normal text-gray-400">(min. 6 Zeichen)</span></Label>
                                    <Input
                                        id="new_password"
                                        type="password"
                                        placeholder="Neues Passwort"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        minLength={6}
                                        data-testid="reset-password"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="new_password_confirm">Passwort wiederholen *</Label>
                                    <Input
                                        id="new_password_confirm"
                                        type="password"
                                        placeholder="Wiederholen"
                                        value={passwordConfirm}
                                        onChange={(e) => setPasswordConfirm(e.target.value)}
                                        required
                                        minLength={6}
                                        data-testid="reset-password-confirm"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full bg-primary hover:bg-primary/80 h-11"
                                    disabled={loading}
                                    data-testid="reset-submit"
                                >
                                    {loading ? (
                                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird gespeichert...</>
                                    ) : (
                                        'Passwort ändern'
                                    )}
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
